import React, { useId } from 'react';

import { TooltipIcon } from '~components';
import { faLock, faPencil } from '~components/icon';

import { ClientInvoicePaymentSource } from '~support-at-home/models';

interface RowLike {
  id?: string | number;
  payment_source?: ClientInvoicePaymentSource;
  [key: string]: unknown;
}

interface ComponentProps {
  // Accept either a row carrying `payment_source` (table-view custom cell
  // shape) or a direct prop. `null` means the invoice has never had its
  // status manually overridden — render nothing rather than confusing the
  // user with a "neutral" indicator.
  row?: RowLike;
  payment_source?: ClientInvoicePaymentSource;
  id?: string;
}

// TAV-2053 source indicator. Distinguishes GoCardless-managed statuses
// (locked from manual overrides) from manually-set statuses.
const PaymentSourceBadge: React.FC<ComponentProps> = ({ row, payment_source, id }) => {
  // Per-instance fallback so two badges rendered without an id/row.id don't
  // collide on the same DOM target — the TooltipPortal looks up its anchor
  // via getElementById, so a duplicate id silently misroutes the tooltip.
  const fallbackId = useId();
  const value = (row?.payment_source as ClientInvoicePaymentSource | undefined) ?? payment_source ?? null;
  if (!value) return null;

  const tooltipId = id ?? (row?.id !== undefined ? String(row.id) : fallbackId);

  if (value === 'gocardless') {
    return (
      <TooltipIcon
        id={`payment-source-${tooltipId}`}
        icon={faLock}
        tooltip="Status set by GoCardless and cannot be manually changed"
        iconColor="dodgerblue"
      />
    );
  }

  return (
    <TooltipIcon
      id={`payment-source-${tooltipId}`}
      icon={faPencil}
      tooltip="Manually set"
      iconColor="gray"
    />
  );
};

export default PaymentSourceBadge;
